import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "@/components/base/Badge";
import CardActions from "@/components/base/CardActions";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import PlatformBienForm from "@/pages/super-admin/components/PlatformBienForm";
import {
  BIEN_STATUT_LABELS,
  BIEN_STATUT_OPTIONS,
  BIEN_STATUT_TONES,
  BIEN_TYPE_ICONS,
  BIEN_TYPE_LABELS,
  BIEN_TYPE_OPTIONS,
  type BienStatut,
  type BienType,
} from "@/pages/proprietaire/biens/types";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import { PLATFORM_SCOPE_LABELS, type PlatformBien, type PlatformScope } from "@/store/platform/types";
import type { ExportColumn } from "@/utils/export";
import { formatFcfa } from "@/utils/format";

type SortKey = "recent" | "loyer" | "reference";

const sortOptions = [
  { value: "recent", label: "Plus récents" },
  { value: "reference", label: "Référence (A → Z)" },
  { value: "loyer", label: "Loyer (décroissant)" },
];

export default function SuperAdminBiensPage() {
  const { biens, getImmeuble, removeBien, countContratsByBien } = usePlatformData();

  const [search, setSearch] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [editing, setEditing] = useState<PlatformBien | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PlatformBien | null>(null);

  const scopeOptions = [
    { value: "", label: "Tous les espaces" },
    ...(Object.keys(PLATFORM_SCOPE_LABELS) as PlatformScope[]).map((scope) => ({
      value: scope,
      label: PLATFORM_SCOPE_LABELS[scope],
    })),
  ];

  const immeubleNom = (immeubleId: string) => getImmeuble(immeubleId)?.nom ?? "Immeuble inconnu";

  const stats = useMemo(
    () => ({
      total: biens.length,
      disponibles: biens.filter((item) => item.statut === "disponible").length,
      occupes: biens.filter((item) => item.statut === "occupe").length,
      loyerTotal: biens.reduce((sum, item) => sum + item.loyerMensuel, 0),
    }),
    [biens]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const result = biens.filter((item) => {
      const matchesScope = !scopeFilter || item.scope === scopeFilter;
      const matchesType = !typeFilter || item.type === typeFilter;
      const matchesStatut = !statutFilter || item.statut === statutFilter;
      const matchesSearch =
        !term ||
        [item.reference, item.description, BIEN_TYPE_LABELS[item.type as BienType], immeubleNom(item.immeubleId)]
          .join(" ")
          .toLowerCase()
          .includes(term);
      return matchesScope && matchesType && matchesStatut && matchesSearch;
    });
    return result.sort((a, b) => {
      if (sort === "loyer") return b.loyerMensuel - a.loyerMensuel;
      if (sort === "reference") return (a.reference || "").localeCompare(b.reference || "", "fr");
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biens, search, scopeFilter, typeFilter, statutFilter, sort]);

  const isFiltering = Boolean(search.trim() || scopeFilter || typeFilter || statutFilter);

  const exportColumns: ExportColumn<PlatformBien>[] = [
    { header: "Référence", value: (item) => item.reference },
    { header: "Type", value: (item) => BIEN_TYPE_LABELS[item.type as BienType] },
    { header: "Immeuble", value: (item) => immeubleNom(item.immeubleId) },
    { header: "Espace", value: (item) => PLATFORM_SCOPE_LABELS[item.scope] },
    { header: "Étage", value: (item) => item.etage },
    { header: "Numéro", value: (item) => item.numero },
    { header: "Surface (m²)", value: (item) => item.surface },
    { header: "Loyer mensuel (FCFA)", value: (item) => item.loyerMensuel },
    { header: "Statut", value: (item) => BIEN_STATUT_LABELS[item.statut as BienStatut] },
  ];

  const resetFilters = () => {
    setSearch("");
    setScopeFilter("");
    setTypeFilter("");
    setStatutFilter("");
  };

  const columns: DataTableColumn<PlatformBien>[] = [
    {
      key: "bien",
      header: "Bien",
      className: "min-w-[200px]",
      render: (bien) => {
        const type = bien.type as BienType;
        return (
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
              <i className={`${BIEN_TYPE_ICONS[type]} text-lg`} aria-hidden="true" />
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-label font-medium text-foreground-950">
                {bien.reference || BIEN_TYPE_LABELS[type]}
              </span>
              <span className="truncate text-xs text-foreground-500">{BIEN_TYPE_LABELS[type]}</span>
            </div>
          </div>
        );
      },
    },
    {
      key: "immeuble",
      header: "Immeuble",
      className: "min-w-[160px]",
      render: (bien) => <span className="text-foreground-700">{immeubleNom(bien.immeubleId)}</span>,
    },
    {
      key: "scope",
      header: "Espace",
      render: (bien) => (
        <Badge tone={bien.scope === "agence" ? "accent" : "primary"}>{PLATFORM_SCOPE_LABELS[bien.scope]}</Badge>
      ),
    },
    {
      key: "loyer",
      header: "Loyer / mois",
      align: "right",
      className: "min-w-[120px]",
      render: (bien) => (
        <span className="font-label font-medium text-foreground-950">{formatFcfa(bien.loyerMensuel)}</span>
      ),
    },
    {
      key: "surface",
      header: "Surface",
      align: "right",
      className: "min-w-[90px]",
      render: (bien) => (
        <span className="text-foreground-700">{bien.surface > 0 ? `${bien.surface} m²` : "—"}</span>
      ),
    },
    {
      key: "localisation",
      header: "Étage / N°",
      className: "min-w-[120px]",
      render: (bien) => {
        const localisation = [bien.etage && `Étage ${bien.etage}`, bien.numero && `N° ${bien.numero}`]
          .filter(Boolean)
          .join(" · ");
        return <span className="text-foreground-700">{localisation || "—"}</span>;
      },
    },
    {
      key: "statut",
      header: "Statut",
      render: (bien) => (
        <Badge tone={BIEN_STATUT_TONES[bien.statut as BienStatut]}>{BIEN_STATUT_LABELS[bien.statut as BienStatut]}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[200px]",
      render: (bien) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/super-admin/biens/${bien.id}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            Voir le détail
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
          <CardActions onEdit={() => setEditing(bien)} onDelete={() => setPendingDelete(bien)} />
        </div>
      ),
    },
  ];

  const confirmDelete = () => {
    if (!pendingDelete) return;
    removeBien(pendingDelete.id);
    setPendingDelete(null);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Biens</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Vue globale de tous les biens de la plateforme, rattachés à leur immeuble. Consultez, corrigez ou supprimez
            un bien — la création se fait depuis l'espace concerné.
          </p>
        </div>
        <ExportMenu
          rows={filtered}
          columns={exportColumns}
          filename="super-admin-biens"
          title="Biens"
          subtitle="Biens affichés selon les filtres"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-home-4-line" label="Biens" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-checkbox-circle-line" label="Disponibles" value={String(stats.disponibles)} tone="secondary" />
        <StatCard icon="ri-user-follow-line" label="Occupés" value={String(stats.occupes)} tone="accent" />
        <StatCard
          icon="ri-money-cny-circle-line"
          label="Loyers cumulés"
          value={new Intl.NumberFormat("fr-FR").format(stats.loyerTotal)}
          hint="FCFA / mois"
          tone="neutral"
        />
      </div>

      {biens.length > 0 && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sa-bien-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="sa-bien-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Référence, immeuble..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sa-bien-scope" className="text-xs font-label font-medium text-foreground-700">
                Espace
              </label>
              <Select
                id="sa-bien-scope"
                value={scopeFilter}
                onChange={(event) => setScopeFilter(event.target.value)}
                options={scopeOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sa-bien-type-filter" className="text-xs font-label font-medium text-foreground-700">
                Type
              </label>
              <Select
                id="sa-bien-type-filter"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}
                options={[{ value: "", label: "Tous les types" }, ...BIEN_TYPE_OPTIONS]}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sa-bien-statut" className="text-xs font-label font-medium text-foreground-700">
                Statut
              </label>
              <Select
                id="sa-bien-statut"
                value={statutFilter}
                onChange={(event) => setStatutFilter(event.target.value)}
                options={[{ value: "", label: "Tous les statuts" }, ...BIEN_STATUT_OPTIONS]}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-500">
              {filtered.length} bien{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {biens.length}
            </p>
            <div className="flex items-center gap-3">
              {isFiltering && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
              <div className="w-48">
                <Select
                  id="sa-bien-sort"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortKey)}
                  options={sortOptions}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {biens.length === 0 && (
        <EmptyState
          icon="ri-home-4-line"
          title="Aucun bien"
          description="Les biens créés par les propriétaires et les agences apparaîtront ici automatiquement."
        />
      )}

      {biens.length > 0 && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun bien ne correspond à votre recherche ou aux filtres sélectionnés."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(bien) => bien.id}
          minWidthClass="min-w-[1240px]"
          caption="Biens"
        />
      )}

      <PlatformBienForm open={Boolean(editing)} onClose={() => setEditing(null)} bien={editing} />

      <ConfirmDeleteModal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        description={
          pendingDelete
            ? `Supprimer le bien « ${pendingDelete.reference || BIEN_TYPE_LABELS[pendingDelete.type as BienType]} » ? Cette action est définitive.`
            : ""
        }
        blockedMessage={
          pendingDelete && countContratsByBien(pendingDelete.id) > 0
            ? `Ce bien est relié à ${countContratsByBien(pendingDelete.id)} contrat(s). Supprimez ou clôturez d'abord ses contrats.`
            : undefined
        }
      />
    </div>
  );
}